import { useEffect, useReducer } from 'react';
import axios from 'axios';

const EVENT = 'adeeb-favorites-change';

let cache = new Set();
let loaded = false;
let inflight = null;

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : null;
}

function emit() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

export async function loadFavorites(force = false) {
  if (loaded && !force) return cache;
  if (inflight) return inflight;
  const headers = authHeaders();
  if (!headers) {
    cache = new Set();
    loaded = true;
    emit();
    return cache;
  }
  inflight = axios
    .get('/api/favorites', { headers })
    .then(({ data }) => {
      cache = new Set((Array.isArray(data) ? data : []).map((f) => String(f.bookId)));
      loaded = true;
      emit();
      return cache;
    })
    .catch(() => {
      cache = new Set();
      loaded = true;
      emit();
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function isFavorite(bookId) {
  return cache.has(String(bookId));
}

export function getFavorites() {
  return new Set(cache);
}

export async function toggleFavorite(bookId) {
  const id = String(bookId);
  const headers = authHeaders();
  if (!headers) {
    const err = new Error('Not authenticated');
    err.code = 'NOT_AUTHENTICATED';
    throw err;
  }
  const wasFav = cache.has(id);
  if (wasFav) cache.delete(id); else cache.add(id);
  emit();
  try {
    if (wasFav) {
      await axios.delete(`/api/favorites/${encodeURIComponent(id)}`, { headers });
    } else {
      await axios.post('/api/favorites', { bookId: id }, { headers });
    }
    return !wasFav;
  } catch (err) {
    if (wasFav) cache.add(id); else cache.delete(id);
    emit();
    throw err;
  }
}

function clearOnLogout() {
  cache = new Set();
  loaded = false;
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('adeeb-auth-change', () => {
    clearOnLogout();
    if (localStorage.getItem('adeeb-token')) loadFavorites(true);
  });
  // When the user switches profiles, drop the old profile's cache and
  // refetch — otherwise the previous profile's favourites leak into the
  // new profile's view until a hard refresh.
  window.addEventListener('adeeb-profile-change', () => {
    clearOnLogout();
    if (localStorage.getItem('adeeb-token')) loadFavorites(true);
  });
}

export function useFavorites() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!loaded) loadFavorites();
    const onChange = () => force();
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return {
    has: (id) => cache.has(String(id)),
    toggle: toggleFavorite,
    all: () => new Set(cache),
  };
}
